// Touch on the board (issue 1178): a finger pans, two pinch, a tap selects, a long press pings or opens a token's menu,
// a finger on your own token drags it, and a tap on your token then a tap on a square moves it there. Only pointers with
// `pointerType === 'touch'` reach this; the mouse and the pen keep their own handling in `BoardCanvas`, unchanged.
// Pure: the recogniser turns pointer facts into intents, and `tapMove` builds exactly the `object.move` a drag sends.
import {
  WALL_IN_THE_WAY, previewRefusal, routeBlocked, type EventBody, type Point, type Wall,
} from '@dragons-table/vtt-shared';
import { capRoute, finishRoute } from './route';
import { mayStep, notYours, type StepThing, type StepWorld } from './keyStep';
import { moveRev } from './group';

/** How far a finger travels before a press is a drag or a pan, not a tap. A finger wobbles more than a mouse. */
export const TOUCH_SLOP_PX = 10;
/** How long a finger is held still to ping, or to open a token's menu: the mouse's hold to ping (issue 1039). */
export const LONG_PRESS_MS = 450;
/** The smallest thing a finger can hit reliably, in screen pixels (WCAG 2.5.5's 44). */
export const MIN_TOUCH_PX = 44;

export interface Pt { x: number; y: number }

/** Two fingers' spread and midpoint. */
export function pinchOf(a: Pt, b: Pt): { dist: number; mid: Pt } {
  return { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
}

/** From one pinch reading to the next: how far the midpoint travelled, and the zoom factor about it. */
export function pinchStep(prev: { dist: number; mid: Pt }, now: { dist: number; mid: Pt }): { dx: number; dy: number; factor: number; mid: Pt } {
  return { dx: now.mid.x - prev.mid.x, dy: now.mid.y - prev.mid.y, factor: prev.dist > 0 && now.dist > 0 ? now.dist / prev.dist : 1, mid: now.mid };
}

export type TouchIntent =
  | { kind: 'press'; x: number; y: number; token: string | null }
  | { kind: 'pinchStart' }
  | { kind: 'pinch'; dx: number; dy: number; factor: number; mid: Pt }
  | { kind: 'pan'; dx: number; dy: number }
  | { kind: 'dragStart'; token: string; x: number; y: number }
  | { kind: 'dragMove'; x: number; y: number }
  | { kind: 'dragEnd'; x: number; y: number }
  | { kind: 'dragCancel' }
  | { kind: 'tap'; x: number; y: number; token: string | null }
  | { kind: 'longPress'; x: number; y: number; token: string | null }
  | { kind: 'release' };

interface Press { pointerId: number; x0: number; y0: number; t0: number; x: number; y: number; token: string | null; mayDrag: boolean; mode: 'press' | 'pan' | 'drag' | 'done' }

/**
 * The gestures, finger by finger. One finger: a press that becomes a tap (released within the slop), a long press
 * (held still), a drag (it began on a token this person may drag) or a pan (anywhere else, a token not yours included).
 * A second finger ends whatever the first was doing — a token drag is cancelled, never dropped — and the two pinch:
 * zoom about their midpoint and pan with it. Fingers lifting out of a pinch do nothing until every finger is up.
 */
export class TouchGestures {
  private pts = new Map<number, Pt>();
  private press: Press | null = null;
  private pinch: { dist: number; mid: Pt } | null = null;

  has(pointerId: number): boolean { return this.pts.has(pointerId); }
  get fingers(): number { return this.pts.size; }

  down(pointerId: number, x: number, y: number, t: number, token: string | null, mayDrag: boolean): TouchIntent[] {
    this.pts.set(pointerId, { x, y });
    if (this.pts.size === 1) {
      this.pinch = null;
      this.press = { pointerId, x0: x, y0: y, t0: t, x, y, token, mayDrag, mode: 'press' };
      return [{ kind: 'press', x, y, token }];
    }
    const out: TouchIntent[] = [];
    if (this.press?.mode === 'drag') out.push({ kind: 'dragCancel' });
    this.press = null;
    if (!this.pinch) {
      const [a, b] = [...this.pts.values()];
      this.pinch = pinchOf(a!, b!);
      out.push({ kind: 'pinchStart' });
    }
    return out;
  }

  move(pointerId: number, x: number, y: number): TouchIntent[] {
    if (!this.pts.has(pointerId)) return [];
    this.pts.set(pointerId, { x, y });
    if (this.pinch) {
      if (this.pts.size < 2) return [];
      const [a, b] = [...this.pts.values()];
      const now = pinchOf(a!, b!);
      const step = pinchStep(this.pinch, now);
      this.pinch = now;
      return [{ kind: 'pinch', ...step }];
    }
    const p = this.press;
    if (!p || p.pointerId !== pointerId || p.mode === 'done') return [];
    if (p.mode === 'press') {
      if (Math.hypot(x - p.x0, y - p.y0) <= TOUCH_SLOP_PX) return [];
      if (p.token && p.mayDrag) { p.mode = 'drag'; return [{ kind: 'dragStart', token: p.token, x: p.x0, y: p.y0 }, { kind: 'dragMove', x, y }]; }
      p.mode = 'pan';
    }
    if (p.mode === 'drag') return [{ kind: 'dragMove', x, y }];
    const dx = x - p.x; const dy = y - p.y;
    p.x = x; p.y = y;
    return [{ kind: 'pan', dx, dy }];
  }

  up(pointerId: number, x: number, y: number, cancelled = false): TouchIntent[] {
    if (!this.pts.has(pointerId)) return [];
    this.pts.delete(pointerId);
    if (this.pinch) {
      if (this.pts.size === 0) { this.pinch = null; return [{ kind: 'release' }]; }
      return [];
    }
    const p = this.press;
    if (!p || p.pointerId !== pointerId) return [];
    this.press = null;
    if (p.mode === 'drag') return [cancelled ? { kind: 'dragCancel' } : { kind: 'dragEnd', x, y }];
    if (p.mode === 'press' && !cancelled) return [{ kind: 'tap', x: p.x0, y: p.y0, token: p.token }];
    return [{ kind: 'release' }];
  }

  /** Called on a timer after a press: a finger held still past LONG_PRESS_MS is a long press, once. */
  hold(t: number): TouchIntent[] {
    const p = this.press;
    if (!p || p.mode !== 'press' || this.pts.size !== 1 || t - p.t0 < LONG_PRESS_MS) return [];
    p.mode = 'done';
    return [{ kind: 'longPress', x: p.x0, y: p.y0, token: p.token }];
  }

  reset(): void { this.pts.clear(); this.press = null; this.pinch = null; }
}

/** A footprint's box widened, for a finger only, to MIN_TOUCH_PX on screen at this zoom; a bigger one is unchanged. */
export function touchBox(fp: { x: number; y: number; width: number; height: number }, zoom: number, min = MIN_TOUCH_PX): { x: number; y: number; width: number; height: number } {
  const want = min / zoom;
  const padX = Math.max(0, (want - fp.width) / 2); const padY = Math.max(0, (want - fp.height) / 2);
  return { x: fp.x - padX, y: fp.y - padY, width: fp.width + 2 * padX, height: fp.height + 2 * padY };
}

/**
 * The token a finger landed on, given each token's footprint in board units: the topmost whose footprint holds the
 * point, else the one whose widened box (`touchBox`) holds it with its centre nearest. `things` is in drawing order,
 * the last on top. The mouse never asks this: it hits the footprint alone (issue 1079).
 */
export function tokenAtTouch(things: ReadonlyArray<{ id: string; fp: { x: number; y: number; width: number; height: number } }>, p: Pt, zoom: number): string | null {
  const inside = (b: { x: number; y: number; width: number; height: number }) => p.x >= b.x && p.x <= b.x + b.width && p.y >= b.y && p.y <= b.y + b.height;
  for (let k = things.length - 1; k >= 0; k -= 1) if (inside(things[k]!.fp)) return things[k]!.id;
  let best: string | null = null; let bestD = Infinity;
  for (const t of things) {
    if (!inside(touchBox(t.fp, zoom))) continue;
    const d = Math.hypot(t.fp.x + t.fp.width / 2 - p.x, t.fp.y + t.fp.height / 2 - p.y);
    if (d < bestD) { bestD = d; best = t.id; }
  }
  return best;
}

export interface TapWorld extends StepWorld { snap: boolean; routeMax: number }

/**
 * Tap to move: the token goes to the square tapped, by the route a drag released there would send — from where it
 * stands straight to the tapped square, snapped as a drag's release is (`finishRoute`), capped as a drag's is. The
 * screen's refusals in a drag's words: not yours, the turn, a wall this screen holds. Null when there is nothing to do
 * (a locked token, or the tap is on its own square).
 */
export function tapMove(o: StepThing, hand: Point, w: TapWorld): { body: EventBody; to: Point; route: Point[] } | { say: string } | null {
  if (o.locked) return null;
  if (!mayStep(o, w)) return { say: notYours(o.name) };
  const refusal = w.encounter ? previewRefusal(w.encounter, o.id, w.isDm || w.movesAny ? 'dm' : 'player') : null;
  if (refusal) return { say: refusal };
  const full = capRoute(finishRoute([{ x: o.x, y: o.y }], hand, w.grid, w.extent, o.size, w.snap), w.routeMax);
  if (full.length < 2) return null;
  if (w.walls.length && routeBlocked({ x: o.x, y: o.y }, full, w.walls as Wall[])) return { say: WALL_IN_THE_WAY };
  const to = full[full.length - 1]!;
  const route = w.placing ? [to] : full;
  return { body: { kind: 'object.move', id: o.id, route, rev: moveRev(o) } as EventBody, to, route };
}

/** Whether a board point is on the board: a tap beyond its edge cancels a tap-to-move. */
export const onBoard = (p: Pt, extent: { width: number; height: number }): boolean => p.x >= 0 && p.y >= 0 && p.x <= extent.width && p.y <= extent.height;

type Listener = (id: string | null) => void;
let armed: string | null = null;
const listeners = new Set<Listener>();
/** The token waiting for its square (tap to move): the canvas rings it, and the hint over the board says what to do. */
export const tapMoveStore = {
  get: (): string | null => armed,
  set(next: string | null): void { if (next === armed) return; armed = next; for (const l of listeners) l(armed); },
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
};


/** A board mode, as far as tap to move cares: whether it is on. */
export interface ModeSource { subscribe(l: (s: { on: boolean }) => void): () => void }

/**
 * A board mode turned on (measuring, an area, pointing…) calls off a token waiting for its square: the mode has the next
 * tap, so the waiting token's ring and its "Tap a square to move" hint would only mislead. Returns the unsubscribe.
 */
export function callOffTapMoveOnModes(modes: ModeSource[]): () => void {
  const offs = modes.map((m) => m.subscribe((s) => { if (s.on) tapMoveStore.set(null); }));
  return () => { for (const off of offs) off(); };
}
